import { createSelector } from '@reduxjs/toolkit';
import { RootState } from './store';
import { MoodleInstanceKey } from './slices/moodleSlice';

export const DEFAULT_MOODLE_INSTANCE: MoodleInstanceKey = 'external';

/* -------------------------------------------------------------------------- */
/*                                   SELECTORS                                */
/* -------------------------------------------------------------------------- */

export const selectInstanceState = (
  state: RootState,
  instance?: MoodleInstanceKey
) =>
  state.moodle.instances[
  instance ?? state.moodle.activeInstance ?? DEFAULT_MOODLE_INSTANCE
    ] ?? state.moodle.instances[DEFAULT_MOODLE_INSTANCE];

export const selectCoursesArr = createSelector(
  [selectInstanceState],
  (instanceState) => Object.values(instanceState?.courses ?? {})
);

export const selectCategoriesArr = createSelector(
  [selectInstanceState],
  (instanceState) => Object.values(instanceState?.categories ?? {})
);

export const selectUsersArr = createSelector(
  [selectInstanceState],
  (instanceState) => Object.values(instanceState?.users ?? {})
);

export const selectLocationsArr = createSelector(
  [selectInstanceState],
  (instanceState) => Object.values(instanceState?.locations ?? {})
);

export const selectSpeakersArr = createSelector(
  [selectInstanceState],
  (instanceState) => Object.values(instanceState?.speakers ?? {})
);

export const selectSponsorsArr = createSelector(
  [selectInstanceState],
  (instanceState) => Object.values(instanceState?.sponsors ?? {})
);

export const selectGalleryArr = createSelector(
  [selectInstanceState],
  (instanceState) => Object.values(instanceState?.gallery ?? {})
);

export const selectJoomlaContentArr = createSelector(
  [selectInstanceState],
  (instanceState) => Object.values(instanceState?.joomlaContent ?? {})
);

export const selectJoomlaCategoriesArr = createSelector(
  [selectInstanceState],
  (instanceState) => Object.values(instanceState?.joomlaCategories ?? {})
);

export const selectTagAreasArr = createSelector(
  [selectInstanceState],
  (instanceState) => Object.values(instanceState?.tagAreas ?? {})
);

export const selectTagCollectionsArr = createSelector(
  [selectInstanceState],
  (instanceState) => Object.values(instanceState?.tagCollections ?? {})
);

export const selectTagsArr = createSelector(
  [selectInstanceState],
  (instanceState) => Object.values(instanceState?.tags ?? {})
);

export const selectJoomlaEventBookingCategoriesArr = createSelector(
  [selectInstanceState],
  (instanceState) => Object.values(instanceState?.joomlaEventBookingCategories ?? {})
);

/* -------------------------------------------------------------------------- */
/*                                  URL HELPERS                               */
/* -------------------------------------------------------------------------- */

function isAbsoluteUrl(url: string): boolean {
  return /^(?:[a-z]+:)?\/\//i.test(url);
}

export function normalizeLeadingSlash(value: string): string {
  return value.startsWith('/') ? value : `/${value}`;
}

function stripOrigin(url: string): string {
  try {
    if (typeof window !== 'undefined') {
      const parsed = new URL(url, window.location.origin);
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

function getDeployment(): string {
  return (process.env.REACT_APP_DEPLOYMENT || '').trim().toLowerCase();
}

function isProductionDeployment(): boolean {
  return getDeployment() === 'production';
}

function getExternalMoodleOrigin(): string {
  const configured = (process.env.REACT_APP_EXTERNAL_MOODLE_ORIGIN || '').trim().replace(/\/+$/, '');
  if (configured) return configured;

  return isProductionDeployment()
    ? 'https://ciscosecurityworkshop.com'
    : 'https://stg.ciscosecurityworkshop.com';
}

function getInternalMoodleOrigin(): string {
  const configured = (process.env.REACT_APP_INTERNAL_MOODLE_ORIGIN || '').trim().replace(/\/+$/, '');
  if (configured) return configured;

  return 'https://cyberacademy.cisco.com';
}

function getMoodleOrigin(instance: MoodleInstanceKey): string {
  return instance === 'internal'
    ? getInternalMoodleOrigin()
    : getExternalMoodleOrigin();
}

function toAbsoluteMoodleUrl(
  rawPath: string,
  instance: MoodleInstanceKey = DEFAULT_MOODLE_INSTANCE
): string {
  const normalizedPath = normalizeMoodlePath(rawPath, instance);
  const origin = getMoodleOrigin(instance);

  if (!origin) {
    return normalizedPath;
  }

  return `${origin}${normalizedPath}`;
}

function getApiBase(): string {
  return (process.env.REACT_APP_API || '').trim().replace(/\/+$/, '');
}

function buildApiUrl(path: string): string {
  const normalizedPath = normalizeLeadingSlash(path);
  const apiBase = getApiBase();

  if (!apiBase) {
    return normalizedPath;
  }

  const apiBaseEndsWithApi = /\/api$/i.test(apiBase);
  const pathStartsWithApi = normalizedPath.startsWith('/api/');

  if (apiBaseEndsWithApi && pathStartsWithApi) {
    return `${apiBase}${normalizedPath.replace(/^\/api/, '')}`;
  }

  return `${apiBase}${normalizedPath}`;
}

function normalizeMoodlePath(path: string, instance: MoodleInstanceKey): string {
  let normalized = stripOrigin(path).trim();

  if (!normalized) return normalized;

  try {
    normalized = decodeURI(normalized);
  } catch {
    // keep original if decoding fails
  }

  normalized = normalizeLeadingSlash(normalized);

  const externalBase = '/workshops';
  const internalBase = '/cyber-academy';
  const expectedBase = instance === 'internal' ? internalBase : externalBase;

  if (
    normalized.startsWith('/workshops/') ||
    normalized === '/workshops' ||
    normalized.startsWith('/cyber-academy/') ||
    normalized === '/cyber-academy'
  ) {
    return normalized;
  }

  if (
    normalized.startsWith('/pluginfile.php') ||
    normalized.startsWith('/webservice/pluginfile.php')
  ) {
    return `${expectedBase}${normalized}`;
  }

  if (normalized.startsWith('/moodle/cyber-academy/')) {
    return normalized.replace(/^\/moodle\/cyber-academy/, '/cyber-academy');
  }

  if (normalized.startsWith('/moodle/')) {
    return normalized.replace(/^\/moodle/, expectedBase);
  }

  return `${expectedBase}${normalized}`;
}

function normalizeAbsoluteAssetUrl(url: string): string {
  try {
    const parsed = new URL(url);

    let pathname = parsed.pathname;
    let search = parsed.search;
    let hash = parsed.hash;

    try {
      pathname = decodeURI(pathname);
    } catch {
      // leave as-is
    }

    try {
      if (search) {
        search = `?${decodeURIComponent(search.slice(1))}`;
      }
    } catch {
      // leave as-is
    }

    try {
      if (hash) {
        hash = `#${decodeURIComponent(hash.slice(1))}`;
      }
    } catch {
      // leave as-is
    }

    return `${parsed.origin}${pathname}${search}${hash}`;
  } catch {
    return url;
  }
}

export function buildProxyHref(
  rawUrl: string,
  instance: MoodleInstanceKey = DEFAULT_MOODLE_INSTANCE
): string {
  if (!rawUrl) return rawUrl;

  let normalized = rawUrl.trim();
  if (!normalized) return normalized;

  if (
    normalized.startsWith('/api/moodle/proxy/file/') ||
    normalized.startsWith('api/moodle/proxy/file/')
  ) {
    return buildApiUrl(normalized);
  }

  if (normalized.startsWith('//')) {
    normalized = `https:${normalized}`;
  }

  if (isAbsoluteUrl(normalized)) {
    const cleanedAbsoluteUrl = normalizeAbsoluteAssetUrl(normalized);
    // Pass the instance so the backend proxy authenticates with the right per-instance
    // WS token (internal Cyber Academy vs. external workshops) instead of guessing.
    return `${buildApiUrl('/api/moodle/proxy/file/')}?url=${encodeURIComponent(cleanedAbsoluteUrl)}&instance=${encodeURIComponent(instance)}`;
  }

  const stripped = stripOrigin(normalized);

  if (
    stripped.includes('pluginfile.php') ||
    stripped.includes('/webservice/pluginfile.php') ||
    stripped.startsWith('/workshops/') ||
    stripped.startsWith('workshops/') ||
    stripped.startsWith('/cyber-academy/') ||
    stripped.startsWith('cyber-academy/') ||
    stripped.startsWith('/moodle/') ||
    stripped.startsWith('moodle/') ||
    stripped.startsWith('/pluginfile.php') ||
    stripped.startsWith('pluginfile.php') ||
    stripped.startsWith('/webservice/pluginfile.php') ||
    stripped.startsWith('webservice/pluginfile.php')
  ) {
    const absoluteUrl = normalizeAbsoluteAssetUrl(toAbsoluteMoodleUrl(stripped, instance));
    return `${buildApiUrl('/api/moodle/proxy/file/')}?url=${encodeURIComponent(absoluteUrl)}&instance=${encodeURIComponent(instance)}`;
  }

  return isAbsoluteUrl(normalized) ? normalized : normalizeLeadingSlash(normalized);
}

function resolveRelativeAssetUrl(
  assetUrl: string,
  pageUrl?: string,
  instance: MoodleInstanceKey = DEFAULT_MOODLE_INSTANCE
): string {
  const trimmed = assetUrl.trim();

  if (!trimmed) return trimmed;

  if (
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:') ||
    trimmed.startsWith('mailto:') ||
    trimmed.startsWith('tel:') ||
    trimmed.startsWith('#')
  ) {
    return trimmed;
  }

  if (trimmed.startsWith('//')) {
    return buildProxyHref(`https:${trimmed}`, instance);
  }

  if (isAbsoluteUrl(trimmed)) {
    return buildProxyHref(trimmed, instance);
  }

  if (trimmed.startsWith('/')) {
    return buildProxyHref(trimmed, instance);
  }

  if (pageUrl) {
    try {
      const base =
        typeof window !== 'undefined'
          ? new URL(pageUrl, window.location.origin)
          : new URL(pageUrl, 'http://localhost');
      const resolved = new URL(trimmed, base).toString();
      return buildProxyHref(resolved, instance);
    } catch {
      return buildProxyHref(trimmed, instance);
    }
  }

  return buildProxyHref(`/moodle/${trimmed}`, instance);
}

/* -------------------------------------------------------------------------- */
/*                                HTML REWRITING                              */
/* -------------------------------------------------------------------------- */

function isStylesheetHref(url: string): boolean {
  const normalized = url.trim().toLowerCase();

  if (!normalized) return false;

  const withoutHash = normalized.split('#')[0];
  const withoutQuery = withoutHash.split('?')[0];

  return (
    withoutQuery.endsWith('.css') ||
    withoutQuery.endsWith('.scss')
  );
}

function stripStylesheetReferences(html: string): string {
  let output = html;

  /**
   * Remove external stylesheet <link> tags:
   *   <link rel="stylesheet" href="...">
   *   <link href="...css">
   *   <link href="...scss">
   */
  output = output.replace(/<link\b[^>]*>/gi, (tag) => {
    const relMatch = tag.match(/\brel=["']([^"']+)["']/i);
    const hrefMatch = tag.match(/\bhref=["']([^"']+)["']/i);

    const rel = relMatch?.[1]?.toLowerCase() ?? '';
    const href = hrefMatch?.[1] ?? '';

    if (rel.includes('stylesheet')) return '';
    if (href && isStylesheetHref(href)) return '';

    return tag;
  });

  /**
   * Remove @import rules that load css/scss files.
   */
  output = output.replace(
    /@import\s+(?:url\()?['"]?([^'")\s]+)['"]?\)?\s*;?/gi,
    (match, url: string) => {
      return isStylesheetHref(url) ? '' : match;
    }
  );

  return output;
}

export function rewritePageHtml(
  inputHtml?: string | null,
  instance: MoodleInstanceKey = DEFAULT_MOODLE_INSTANCE,
  pageUrl?: string
): string {
  if (!inputHtml) return '';

  let html = String(inputHtml);

  html = stripStylesheetReferences(html);

  const pluginBase =
    instance === 'internal'
      ? '/moodle/cyber-academy/pluginfile.php/'
      : '/moodle/pluginfile.php/';

  html = html.replace(
    /@@PLUGINFILE@@([^\s"'()<>]+)/g,
    (_match, rest: string) =>
      buildProxyHref(`${pluginBase}${rest.replace(/^\/+/, '')}`, instance)
  );

  html = html.replace(
    /\b(src|href|poster)=["']([^"']+)["']/gi,
    (_match, attr: string, url: string) => {
      const rewritten = resolveRelativeAssetUrl(url, pageUrl, instance);
      return `${attr}="${rewritten}"`;
    }
  );

  html = html.replace(
    /url\((['"]?)([^'")]+)\1\)/gi,
    (_match, quote: string, url: string) => {
      const rewritten = resolveRelativeAssetUrl(url, pageUrl, instance);
      return `url(${quote}${rewritten}${quote})`;
    }
  );

  return html;
}

/* -------------------------------------------------------------------------- */
/*                           LEGACY/COMPATIBILITY HELPERS                     */
/* -------------------------------------------------------------------------- */

export function rewriteMoodleHtmlImages(
  inputHtml?: string | null,
  instance: MoodleInstanceKey = DEFAULT_MOODLE_INSTANCE,
  pageUrl?: string
): string {
  return rewritePageHtml(inputHtml, instance, pageUrl);
}

export function rewriteTemplateHtml(
  inputHtml?: string | null,
  instance: MoodleInstanceKey = DEFAULT_MOODLE_INSTANCE,
  pageUrl?: string
): string {
  return rewritePageHtml(inputHtml, instance, pageUrl);
}
